import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, put, post } from '@/lib/api'

import { issueKeys } from './use-issues'

// ==================== Types ====================

export type Stage = 'development' | 'code_review' | 'testing'

export interface StageAssignments {
  issueId: string
  developmentAssigneeId: string | null
  codeReviewAssigneeId: string | null
  testingAssigneeId: string | null
  createdAt: string
  updatedAt: string | null
}

export interface StageAssignmentInput {
  developmentAssigneeId?: string | null
  codeReviewAssigneeId?: string | null
  testingAssigneeId?: string | null
}

export interface BatchStageConfigInput {
  issueIds: string[]
  developmentAssigneeId?: string | null
  codeReviewAssigneeId?: string | null
  testingAssigneeId?: string | null
}

export interface BatchConfigureItemResult {
  issueId: string
  success: boolean
  error?: string
}

export interface BatchConfigureResult {
  success: boolean
  successCount: number
  failureCount: number
  results: BatchConfigureItemResult[]
}

// ==================== Query Keys ====================

export const stageAssignmentKeys = {
  all: ['stage-assignments'] as const,
  lists: () => [...stageAssignmentKeys.all, 'list'] as const,
  list: (issueId: string) => [...stageAssignmentKeys.lists(), issueId] as const,
}

// ==================== Hooks ====================

/**
 * 获取任务阶段分配
 * Requirements: 1.1
 */
export function useStageAssignments(issueId: string) {
  return useQuery({
    queryKey: stageAssignmentKeys.list(issueId),
    queryFn: () => get<StageAssignments | null>(`/pm/stage-assignments/issues/${issueId}`),
    enabled: !!issueId,
  })
}

/**
 * 更新任务阶段分配
 * Requirements: 1.1
 */
export function useUpdateStageAssignments() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: StageAssignmentInput }) =>
      put<StageAssignments>(`/pm/stage-assignments/issues/${issueId}`, data),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: stageAssignmentKeys.list(result.issueId) })
      void queryClient.invalidateQueries({ queryKey: issueKeys.detail(result.issueId) })
      void queryClient.invalidateQueries({ queryKey: issueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['my-stage-tasks'] })
    },
  })
}

/**
 * 批量配置阶段分配
 * Requirements: 6.3
 */
export function useBatchStageConfig() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BatchStageConfigInput) =>
      post<BatchConfigureResult>('/pm/stage-assignments/batch', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: stageAssignmentKeys.all })
      void queryClient.invalidateQueries({ queryKey: issueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['my-stage-tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['issues', 'kanban'] })
    },
  })
}
